(() => {
  function init(root) {
    if (root.dataset.ready) return;
    root.dataset.ready = 'true';
    const find = selector => root.querySelector(selector);
    const strings = JSON.parse(find('[data-archive-strings]').textContent);
    const firstPage = JSON.parse(find('[data-archive-page]').textContent);
    const grid = find('[data-grid]');
    const status = find('[data-status]');
    const more = find('[data-load-more]');
    const retry = find('[data-archive-retry]');
    const dialog = find('[data-lightbox]');
    const stage = find('[data-stage]');
    const pageSize = 24;
    let items = [], filtered = [], filter = 'all', visible = pageSize;
    let viewer = [], position = 0, opener, touch, loadBusy = false, revision = 0;
    const projectHandle = new URL(location.href).searchParams.get('project');
    const imageURL = (value, width) => {
      const url = new URL(value, location.origin);
      if (!['https:', 'http:'].includes(url.protocol)) throw Error('Invalid media URL');
      url.searchParams.set('width', String(width));
      return url.href;
    };
    const element = (tag, className, text) => {
      const el = document.createElement(tag);
      if (className) el.className = className;
      if (text) el.textContent = text;
      return el;
    };
    const sorted = list => [...list].sort((a,b) => (Number(a.order ?? 9999) - Number(b.order ?? 9999)) || String(b.date || '').localeCompare(String(a.date || '')) || String(a.handle).localeCompare(String(b.handle)));
    function makeImage(item, full = false) {
      const img = element('img');
      img.src = imageURL(item.image.url, full ? 2000 : 900);
      img.srcset = (full ? [800,1200,1600,2000,2400] : [360,540,720,900,1200]).map(w => imageURL(item.image.url,w) + ' ' + w + 'w').join(',');
      img.sizes = full ? '92vw' : '(max-width:479px) calc(100vw - 40px), (max-width:1000px) 45vw, (min-width:1500px) 340px, 30vw';
      img.width = item.image.width || 800;
      img.height = item.image.height || 1000;
      img.alt = item.image.alt || item.title;
      img.loading = full ? 'eager' : 'lazy';
      img.decoding = 'async';
      img.addEventListener('error', () => {
        img.dataset.failed = 'true';
        if (full) find('[data-player-status]').textContent = strings.image_error;
      }, {once:true});
      return img;
    }
    function meta(item) {
      return [item.project?.title, item.category, item.date].filter(Boolean).join(' / ');
    }
    function card(item) {
      const article = element('article','archive-card');
      const button = element('button','archive-open');
      button.type = 'button';
      button.setAttribute('aria-label', strings.open + ' ' + item.title);
      button.setAttribute('aria-haspopup','dialog');
      const frame = element('span','archive-frame');
      frame.append(makeImage(item), element('span','archive-type',strings[item.type].toUpperCase()));
      if (item.type === 'video') {
        const play = element('span','archive-play','▶');
        play.setAttribute('aria-hidden','true');
        frame.append(play);
      }
      const caption = element('span','archive-caption');
      caption.append(element('span','archive-caption-title',item.title),element('span','archive-caption-meta',meta(item)));
      button.append(frame,caption);
      button.addEventListener('click', () => open(item,button));
      article.append(button);
      return article;
    }
    function render(append = false) {
      filtered = items.filter(item => (filter === 'all' || item.type === filter) && (!projectHandle || item.project?.handle === projectHandle));
      const start = append ? grid.children.length : 0;
      if (!append) grid.replaceChildren();
      const fragment = document.createDocumentFragment();
      filtered.slice(start,visible).forEach(item => fragment.append(card(item)));
      grid.append(fragment);
      const featured = filtered.filter(item => item.featured === true).slice(0,6);
      if (!append) find('[data-featured]').replaceChildren(...featured.map(card));
      find('[data-featured-section]').hidden = !featured.length;
      find('[data-archive-mark]').textContent = featured.length ? '02' : '01';
      more.hidden = visible >= filtered.length;
      find('[data-count]').textContent = filtered.length + ' ' + strings.items;
      status.textContent = filtered.length ? '' : strings.empty;
      status.hidden = Boolean(filtered.length);
      grid.setAttribute('aria-busy','false');
      if (append) {
        find('[data-count]').textContent = strings.showing + ' ' + Math.min(visible,filtered.length) + ' ' + strings.of + ' ' + filtered.length;
        grid.children[start]?.querySelector('button')?.focus({preventScroll:true});
      }
    }
    async function load() {
      if (loadBusy) return;
      loadBusy = true;
      retry.hidden = true;
      status.hidden = false;
      status.textContent = strings.loading;
      grid.setAttribute('aria-busy','true');
      try {
        const records = new Map();
        const visited = new Set();
        let page = firstPage;
        while (page) {
          if (!Array.isArray(page.items)) throw Error('Invalid archive');
          page.items.forEach(item => records.set(item.id,item));
          if (!page.next) break;
          const next = new URL(page.next,location.href);
          if (next.origin !== location.origin || visited.has(next.href)) throw Error('Invalid archive pagination');
          visited.add(next.href);
          next.searchParams.set('section_id',root.dataset.sectionId);
          const preview = new URL(location.href).searchParams.get('preview_theme_id');
          if (preview) next.searchParams.set('preview_theme_id',preview);
          const response = await fetch(next,{credentials:'same-origin',signal:AbortSignal.timeout(15000)});
          if (!response.ok) throw Error('Archive unavailable');
          const parsed = new DOMParser().parseFromString(await response.text(),'text/html');
          const payload = parsed.querySelector('[data-archive-page]');
          if (!payload) throw Error('Archive unavailable');
          page = JSON.parse(payload.textContent);
        }
        if (!root.isConnected) return;
        items = sorted([...records.values()].filter(item => ['photo','video'].includes(item.type) && item.image?.url && (item.type !== 'video' || item.sources?.length)));
        render();
      } catch {
        status.textContent = strings.error;
        retry.hidden = false;
        grid.setAttribute('aria-busy','false');
      } finally { loadBusy = false; }
    }
    function unloadPlayer() {
      revision++;
      const video = stage.querySelector('video');
      if (video) { video.pause(); video.removeAttribute('src'); video.replaceChildren(); video.load(); }
      stage.replaceChildren();
    }
    function show() {
      unloadPlayer();
      const currentRevision = revision;
      const item = viewer[position];
      find('#archive-lightbox-title').textContent = item.title;
      find('[data-lightbox-project]').textContent = meta(item);
      find('[data-description]').textContent = item.description || '';
      find('[data-player-status]').textContent = '';
      find('[data-position]').textContent = String(position + 1).padStart(2,'0') + ' / ' + String(viewer.length).padStart(2,'0');
      find('[data-prev]').disabled = position === 0;
      find('[data-next]').disabled = position === viewer.length - 1;
      if (item.type === 'photo') { stage.append(makeImage(item,true)); return; }
      const video = element('video');
      video.controls = true;
      video.playsInline = true;
      video.preload = 'none';
      video.poster = imageURL(item.image.url,1200);
      video.setAttribute('aria-label',item.title);
      const mp4 = item.sources.filter(source => source.type === 'video/mp4').sort((a,b) => a.height - b.height);
      const limit = matchMedia('(max-width:700px)').matches ? 720 : 1080;
      const source = mp4.filter(s => s.height <= limit).at(-1) || mp4[0] || item.sources.find(s => video.canPlayType(s.type));
      if (!source) { find('[data-player-status]').textContent = strings.play_error; return; }
      video.src = new URL(source.url,location.origin).href;
      const failed = () => { if (revision === currentRevision && dialog.open) find('[data-player-status]').textContent = strings.play_error; };
      video.addEventListener('error',failed);
      stage.append(video);
      video.play().catch(failed);
    }
    function open(item,button) {
      opener = button;
      viewer = filtered.filter(record => record.type === item.type);
      position = viewer.findIndex(record => record.id === item.id);
      document.documentElement.classList.add('archive-lightbox-open');
      dialog.showModal();
      show();
      find('[data-lightbox-close]').focus();
    }
    function move(direction) {
      const target = position + direction;
      if (target < 0 || target >= viewer.length) return;
      position = target;
      show();
    }
    root.querySelectorAll('[data-filter]').forEach(button => button.addEventListener('click', () => {
      filter = button.dataset.filter;
      visible = pageSize;
      root.querySelectorAll('[data-filter]').forEach(b => b.setAttribute('aria-pressed', String(b === button)));
      if (!loadBusy) render();
    }));
    more.addEventListener('click', () => { visible += pageSize; render(true); });
    retry.addEventListener('click',load);
    find('[data-lightbox-close]').addEventListener('click',() => dialog.close());
    find('[data-prev]').addEventListener('click',() => move(-1));
    find('[data-next]').addEventListener('click',() => move(1));
    dialog.addEventListener('close', () => {
      unloadPlayer();
      document.documentElement.classList.remove('archive-lightbox-open');
      if (opener?.isConnected) opener.focus({preventScroll:true});
    });
    dialog.addEventListener('keydown',event => {
      if (event.target.tagName === 'VIDEO') return;
      if (event.key === 'ArrowRight') { event.preventDefault(); move(1); }
      if (event.key === 'ArrowLeft') { event.preventDefault(); move(-1); }
    });
    stage.addEventListener('touchstart', event => {
      touch = event.touches.length === 1 && viewer[position]?.type === 'photo' ? {x:event.touches[0].clientX,y:event.touches[0].clientY} : null;
    }, {passive:true});
    stage.addEventListener('touchend', event => {
      if (!touch || !event.changedTouches.length) return;
      const dx = event.changedTouches[0].clientX-touch.x, dy = event.changedTouches[0].clientY-touch.y;
      if (Math.abs(dx)>60 && Math.abs(dx)>Math.abs(dy)*1.5) move(dx<0?1:-1);
      touch=null;
    }, {passive:true});
    stage.addEventListener('touchcancel', () => { touch=null; }, {passive:true});
    document.addEventListener('visibilitychange', () => { if(document.hidden) stage.querySelector('video')?.pause(); });
    load();
  }
  document.querySelectorAll('[data-antics-archive]').forEach(init);
  if (!window.anticsArchiveEditorBound) {
    window.anticsArchiveEditorBound = true;
    document.addEventListener('shopify:section:load', event => event.target.querySelectorAll('[data-antics-archive]').forEach(init));
  }
})();
